import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import {
    Notice,
    CreateNoticeRequestDto,
    UpdateNoticeRequestDto,
    TargetType,
    TargetScope,
    TARGET_TYPE_OPTIONS,
    TARGET_SCOPE_OPTIONS,
} from '../types/notice.types';
import { SUPPLIER_STATUS_CODES, SUPPLIER_STATUS_SORT_ORDER } from '@/features/partner/supplierStatus/constants/supplierStatus.constants';
import { ACTIVE_RIDER_STATUSES } from '@/features/rider/riderStatus/constants/riderStatus.constants';
import { getItem } from '@/shared/utils/storage';
import { supplierApi } from '@/features/partner/supplierStatus/api/supplierApi';
import { getSupplierStatusLabel } from '@/shared/utils/labelConverterHelpers';
import { riderApi } from '@/features/rider/api/riderApi';

export interface SupplierOption {
    value: string;
    label: string;
    statusCode: string;
    status: string;
    isOperating: boolean;
}

export interface RiderOption {
    value: string;
    label: string;
    riderId: string;
    riderName: string;
    suppId: string;
    suppName: string;
    status: string;
    isActive: boolean;
}

interface NoticeFormData {
    title: string;
    content: string;
    targetType: TargetType;
    targetScope: TargetScope;
    targetIds: string[];
    startDate: string;
    endDate: string;
    popupYn: string;
    urgentYn: string;
    useYn: string;
}

export const useNoticeForm = (
    notice: Notice | null,
    onSave: (data: CreateNoticeRequestDto | UpdateNoticeRequestDto) => void,
) => {
    const isEdit = !!notice;
    const userType = getItem<string>('USER_TYPE');
    const isSupplierUser = userType === 'SUPPLIER' || userType === 'S';

    const [formData, setFormData] = useState<NoticeFormData>({
        title: '',
        content: '',
        targetType: isSupplierUser ? 'R' : 'A',
        targetScope: 'ALL',
        targetIds: [],
        startDate: '',
        endDate: '',
        popupYn: 'Y',
        urgentYn: 'N',
        useYn: 'Y',
    });

    const [supplierOptions, setSupplierOptions] = useState<SupplierOption[]>([]);
    const [riderOptions, setRiderOptions] = useState<RiderOption[]>([]);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [riderFilterSuppId, setRiderFilterSuppId] = useState('');

    const loadSuppliers = useCallback(async () => {
        try {
            setOptionsLoading(true);
            const response = await supplierApi.getSupplierDetailList();
            const suppliers = response.suppliers ?? [];
            const options: SupplierOption[] = suppliers
                .filter((s) => s.suppId && s.suppName)
                .map((s) => {
                    const statusCode = s.statusCode || '';
                    return {
                        value: s.suppId,
                        label: s.suppName,
                        statusCode,
                        status: getSupplierStatusLabel(statusCode),
                        isOperating: statusCode === SUPPLIER_STATUS_CODES.OPERATING,
                    };
                })
                .sort((a, b) => {
                    const orderA = SUPPLIER_STATUS_SORT_ORDER[a.statusCode] ?? 999;
                    const orderB = SUPPLIER_STATUS_SORT_ORDER[b.statusCode] ?? 999;
                    if (orderA !== orderB) return orderA - orderB;
                    return a.label.localeCompare(b.label);
                });
            setSupplierOptions(options);
        } catch {
            setSupplierOptions([]);
        } finally {
            setOptionsLoading(false);
        }
    }, []);

    const loadRiders = useCallback(async () => {
        try {
            setOptionsLoading(true);
            const response = await riderApi.getRiderList();
            const riders = Array.isArray(response) ? response : [];
            const options: RiderOption[] = riders
                .filter((r) => {
                    if (!r.riderId || !r.name) return false;
                    const statusName = r.contractStatusName || r.status?.description || '';
                    return ACTIVE_RIDER_STATUSES.includes(statusName);
                })
                .map((r) => {
                    const statusName = r.contractStatusName || r.status?.description || '';
                    const supplierName = r.suppName || r.centerName || '-';
                    return {
                        value: r.riderId!,
                        label: `${r.riderId} (${r.name}) (${supplierName})`,
                        riderId: r.riderId!,
                        riderName: r.name!,
                        suppId: r.suppId || '',
                        suppName: supplierName,
                        status: statusName,
                        isActive: statusName === '운영 중',
                    };
                });
            setRiderOptions(options);
        } catch {
            setRiderOptions([]);
        } finally {
            setOptionsLoading(false);
        }
    }, []);

    useEffect(() => {
        const needsSupplierList =
            formData.targetScope === 'INDIVIDUAL' &&
            (formData.targetType === 'S' || formData.targetType === 'SR');
        const needsRiderList =
            formData.targetScope === 'INDIVIDUAL' && formData.targetType === 'R';
        if (needsSupplierList) loadSuppliers();
        if (needsRiderList) {
            loadSuppliers();
            loadRiders();
        }
    }, [formData.targetType, formData.targetScope, loadSuppliers, loadRiders]);

    useEffect(() => {
        if (!notice) return;
        setFormData({
            title: notice.title,
            content: notice.content,
            targetType: notice.targetType,
            targetScope: notice.targetScope,
            targetIds: notice.targetIds ?? [],
            startDate: notice.startDate ? format(notice.startDate, "yyyy-MM-dd'T'HH:mm") : '',
            endDate: notice.endDate ? format(notice.endDate, "yyyy-MM-dd'T'HH:mm") : '',
            popupYn: notice.popupYn,
            urgentYn: notice.urgentYn ?? 'N',
            useYn: notice.useYn,
        });
    }, [notice]);

    // --- Computed values (useMemo 선언 후 핸들러에서 참조) ---

    const availableTargetTypeOptions = useMemo(
        () =>
            isSupplierUser
                ? TARGET_TYPE_OPTIONS.filter((o) => o.value === 'R')
                : TARGET_TYPE_OPTIONS,
        [isSupplierUser],
    );

    const availableTargetScopeOptions = useMemo(
        () =>
            formData.targetType === 'A' || formData.targetType === 'R'
                ? [{ value: 'ALL', label: '전체' }]
                : TARGET_SCOPE_OPTIONS,
        [formData.targetType],
    );

    const filteredSupplierOptions = useMemo(() => {
        const notSelected = supplierOptions.filter(
            (o) => !formData.targetIds.includes(o.value),
        );
        if (!searchKeyword.trim()) return notSelected;
        return notSelected.filter(
            (o) =>
                o.isOperating &&
                (o.label.toLowerCase().includes(searchKeyword.toLowerCase()) ||
                    o.value.toLowerCase().includes(searchKeyword.toLowerCase())),
        );
    }, [supplierOptions, formData.targetIds, searchKeyword]);

    const filteredRiderOptions = useMemo(() => {
        let filtered = riderOptions.filter((o) => !formData.targetIds.includes(o.value));
        if (riderFilterSuppId) filtered = filtered.filter((o) => o.suppId === riderFilterSuppId);
        if (searchKeyword.trim()) {
            filtered = filtered.filter(
                (o) =>
                    o.label.toLowerCase().includes(searchKeyword.toLowerCase()) ||
                    o.riderId.toLowerCase().includes(searchKeyword.toLowerCase()) ||
                    o.riderName.toLowerCase().includes(searchKeyword.toLowerCase()),
            );
        }
        return filtered;
    }, [riderOptions, formData.targetIds, riderFilterSuppId, searchKeyword]);

    const currentOptionsLabel = useMemo(() => {
        if (formData.targetType === 'S' || formData.targetType === 'SR') return '협력사';
        if (formData.targetType === 'R') return '라이더';
        return '';
    }, [formData.targetType]);

    const selectedItems = useMemo(() => {
        if (formData.targetType === 'S' || formData.targetType === 'SR')
            return supplierOptions.filter((o) => formData.targetIds.includes(o.value));
        if (formData.targetType === 'R')
            return riderOptions.filter((o) => formData.targetIds.includes(o.value));
        return [];
    }, [formData.targetType, formData.targetIds, supplierOptions, riderOptions]);

    const showIndividualSelection =
        formData.targetScope === 'INDIVIDUAL' &&
        (formData.targetType === 'S' ||
            formData.targetType === 'SR' ||
            formData.targetType === 'R');

    const currentFilteredOptions =
        formData.targetType === 'R' ? filteredRiderOptions : filteredSupplierOptions;
    const currentTotalOptions =
        formData.targetType === 'R' ? riderOptions : supplierOptions;

    // --- 이벤트 핸들러 ---

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ) => {
        const { name, value } = e.target;
        if (name === 'targetType') {
            setFormData((prev) => ({
                ...prev,
                [name]: value as TargetType,
                targetIds: [],
                targetScope: 'ALL',
            }));
            setSearchKeyword('');
            setRiderFilterSuppId('');
            return;
        }
        if (name === 'targetScope') {
            setFormData((prev) => ({
                ...prev,
                [name]: value as TargetScope,
                targetIds: [],
            }));
            setSearchKeyword('');
            setRiderFilterSuppId('');
            return;
        }
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.title.trim()) { alert('제목을 입력해주세요.'); return; }
        if (!formData.content.trim()) { alert('내용을 입력해주세요.'); return; }
        const needsSelection =
            formData.targetScope === 'INDIVIDUAL' &&
            (formData.targetType === 'S' ||
                formData.targetType === 'SR' ||
                formData.targetType === 'R');
        if (needsSelection && formData.targetIds.length === 0) {
            alert('대상을 선택해주세요.');
            return;
        }
        onSave({
            ...(isEdit && { noticeId: notice!.noticeId }),
            title: formData.title,
            content: formData.content,
            targetType: formData.targetType,
            targetScope: formData.targetScope,
            targetIds: formData.targetScope === 'INDIVIDUAL' ? formData.targetIds : undefined,
            startDate: formData.startDate || undefined,
            endDate: formData.endDate || undefined,
            popupYn: formData.popupYn,
            urgentYn: isSupplierUser ? 'N' : formData.urgentYn,
            useYn: formData.useYn,
        });
    };

    const handleAddTarget = (value: string) => {
        if (!formData.targetIds.includes(value)) {
            setFormData((prev) => ({ ...prev, targetIds: [...prev.targetIds, value] }));
        }
    };

    const handleRemoveTarget = (value: string) => {
        setFormData((prev) => ({
            ...prev,
            targetIds: prev.targetIds.filter((id) => id !== value),
        }));
    };

    const handleSelectAll = () => {
        const allValues = currentFilteredOptions.map((o) => o.value);
        setFormData((prev) => ({
            ...prev,
            targetIds: [...new Set([...prev.targetIds, ...allValues])],
        }));
    };

    const handleClearAll = () => {
        setFormData((prev) => ({ ...prev, targetIds: [] }));
    };

    return {
        isEdit,
        isSupplierUser,
        formData,
        supplierOptions,
        optionsLoading,
        searchKeyword,
        riderFilterSuppId,
        availableTargetTypeOptions,
        availableTargetScopeOptions,
        currentOptionsLabel,
        selectedItems,
        showIndividualSelection,
        currentFilteredOptions,
        currentTotalOptions,
        handleChange,
        handleSubmit,
        handleAddTarget,
        handleRemoveTarget,
        handleSelectAll,
        handleClearAll,
        setSearchKeyword,
        setRiderFilterSuppId,
    };
};
